import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';

import { setNavbarToggler } from './util';
import navItems from '../../routes';
import logo from '../../images/icon-tertiary.png';

function Navbar() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    setNavbarToggler();
    
    // Handle scroll effect
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const isHomePage = location.pathname === '/';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled || !isHomePage 
        ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200' 
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className={`flex items-center space-x-3 no-underline transition-colors duration-200 ${
              isScrolled || !isHomePage 
                ? 'text-primary hover:text-primary-dark'
                : 'text-white hover:text-blue-200'
            }`}
          >
            <img
              className="h-8 lg:h-10 w-auto"
              src={logo}
              alt="Swami Ayurveda"
            />
            <span className="font-display text-xl lg:text-2xl font-semibold">
              Swami Ayurveda
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map(item => {
              const Component = item.href ? 'a' : Link;
              const isActive = !item.href && location.pathname === item.to;
              
              return (
                <Component
                  key={item.to || item.href}
                  className={`font-body font-medium transition-all duration-200 no-underline relative group ${
                    isScrolled || !isHomePage
                      ? isActive 
                        ? 'text-primary' 
                        : 'text-gray-700 hover:text-primary'
                      : isActive
                        ? 'text-green-200'
                        : 'text-white/90 hover:text-white'
                  }`}
                  to={item.to}
                  href={item.href}
                  target={item.href ? '_blank' : undefined}
                  rel={item.href ? 'noopener noreferrer' : undefined}
                >
                  <span className="relative z-10">{item.title}</span>
                  {/* Underline effect */}
                  <span className={`absolute bottom-0 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full ${
                    isScrolled || !isHomePage ? 'bg-primary' : 'bg-green-200'
                  }`}></span>
                </Component>
              );
            })}
          </div>

          {/* CTA Button (Desktop) */}
          <div className="hidden lg:block">
            <a
              href="https://www.fresha.com/providers/swami-ayurveda-ia5mxbwa?allOffer=true"
              target="_blank"
              rel="noopener noreferrer"
              className={`font-body font-semibold px-6 py-2.5 rounded-full transition-all duration-200 no-underline transform hover:scale-105 ${
                isScrolled || !isHomePage
                  ? 'bg-primary text-white hover:bg-primary-dark shadow-md hover:shadow-lg'
                  : 'bg-white/20 text-white hover:bg-white hover:text-primary backdrop-blur-sm border border-white/30'
              }`}
            >
              Book Now
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className={`lg:hidden p-2 rounded-md transition-colors duration-200 ${
              isScrolled || !isHomePage
                ? 'text-gray-700 hover:text-primary hover:bg-gray-100'
                : 'text-white hover:text-blue-200 hover:bg-white/10'
            }`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg 
              className="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              {isMenuOpen ? (
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M6 18L18 6M6 6l12 12" 
                />
              ) : (
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 6h16M4 12h16M4 18h16" 
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden transition-all duration-300 ease-in-out ${
          isMenuOpen 
            ? 'max-h-96 opacity-100' 
            : 'max-h-0 opacity-0 overflow-hidden'
        }`}>
          <div className="py-4 space-y-2 bg-white/95 backdrop-blur-md rounded-lg mt-2 border border-gray-200 shadow-lg">
            {navItems.map(item => {
              const Component = item.href ? 'a' : Link;
              const isActive = !item.href && location.pathname === item.to;
              
              return (
                <Component
                  key={item.to || item.href}
                  className={`block px-4 py-3 font-body font-medium transition-colors duration-200 no-underline ${
                    isActive 
                      ? 'text-primary bg-primary/5 border-r-4 border-primary' 
                      : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                  }`}
                  to={item.to}
                  href={item.href}
                  target={item.href ? '_blank' : undefined}
                  rel={item.href ? 'noopener noreferrer' : undefined}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.title}
                </Component>
              );
            })}
            
            {/* Mobile CTA */}
            <div className="px-4 pt-4 border-t border-gray-200">
              <a
                href="https://www.fresha.com/providers/swami-ayurveda-ia5mxbwa?allOffer=true"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center bg-primary text-white font-semibold py-3 px-6 rounded-full no-underline transition-all duration-200 hover:bg-primary-dark transform hover:scale-105"
                onClick={() => setIsMenuOpen(false)}
              >
                Book Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
